import React, { useState } from 'react';
import PropTypes from 'prop-types';

import FontAwesome from 'utils/FA';
import classNames from 'classnames';
import moment from 'moment';
import { momentFormat, queueTitle } from './helpers';

import './styles/delayed_jobs.scss';
import ContactList from '../ContactList/ContactList';
import { ContactRow } from '../ContactList/ContactRows';


/**
 * Renders a table of jobs on each queue grouped by priority
 *
 * @param queueSummaries Dictionary with queues as keys pointing to jobs within that queue
 *
 * @constructor
 */
const QueueSummaryTable = ({ queueSummaries }) => {
  const summaries = [];

  Object.keys(queueSummaries)
    .forEach((queue) => {
      const queueInfo = queueSummaries[queue];
      queueInfo.queueName = queue;
      queueInfo.id = queue;
      summaries.push(queueInfo);
    });


  /** The list of columns to show for this page */
  const columns = [
    {
      header: '',
      isExpander: true,
      value: <td className='delayed-jobs-expander-column' />
    },
    {
      header: 'Queue',
      value: (item) => (
        <td>
          {queueTitle(item.queueName)}
          &nbsp;Jobs
        </td>
      ),
      isQueueColumn: true
    },
    {
      header: 'Priority',
      value: (item) => (
        <td>{item.priority}</td>
      )
    },
    {
      header: 'Count',
      value: (item) => (
        <td>{item.count}</td>
      )
    },
    {
      header: 'Oldest',
      value: (item) => (
        <td>{item.oldest !== undefined ? moment(item.oldest).calendar(null, momentFormat) : ''}</td>
      )
    }
  ];

  return (
    <ContactList
      columns={columns}
      className='delayed-jobs-priority-table'
      contacts={summaries}
      emptyMessage='There are no jobs running.'
      makeRow={(props) => <QueueRow {...props} />}
    />
  );
};

/**
 * Renders a collapsible jobs by queue and priority row
 *
 * @param contact   A queueSummary, but matching ContactList's naming scheme
 * @param columns   Columns to render in table
 */
const QueueRow = ({ contact, columns }) => {
  const [expanded, setExpanded] = useState(false);

  /** Add expander and link to queue in main row */
  const expandoColumns = columns.map(
    (col) => {
      if (col.isExpander) {
        return {
          value: (item) => (item.count > 0 ? (
            <td
              className={classNames(
                'contact-list-row-expander',
                { expanded, collapsed: !expanded },
                'delayed-jobs-expander-column'
              )}
            >
              <button
                className='btn btn-xs'
                type='button'
                onClick={() => setExpanded(!expanded)}
              >
                <FontAwesome name={expanded ? 'caret-down' : 'caret-right'} />
              </button>
            </td>
          ) : <td className='delayed-jobs-expander-column' />)

        };
      } else if (col.isQueueColumn) {
        return {
          value: (item) => (
            <td>
              <strong>
                <a href={`#${item.queueName}`}>
                  {queueTitle(item.queueName)}
                  &nbsp;Jobs
                </a>
              </strong>
            </td>
          )
        };
      }
      return col;
    }
  );

  return (
    <React.Fragment>
      <ContactRow contact={contact} columns={expandoColumns} className='delayed-jobs-priority-row' />
      {expanded && contact.priorities.map(priorityRow => (
        <ContactRow
          contact={priorityRow}
          columns={columns}
          key={`${priorityRow.queueName}-${priorityRow.priority.toString()}`}
          className='delayed-jobs-priority-subrow'
        />
      ))
      }
    </React.Fragment>
  );
};

QueueSummaryTable.propTypes = {
  queueSummaries: PropTypes.objectOf(PropTypes.shape({
    priorities: PropTypes.arrayOf(PropTypes.shape({
      oldest: PropTypes.string,
      count: PropTypes.number,
      queueName: PropTypes.string,
      priority: PropTypes.number
    })),
    queueName: PropTypes.string,
    count: PropTypes.number,
    oldest: PropTypes.string,
    id: PropTypes.string
  }))
};

QueueSummaryTable.defaultProps = {
  queueSummaries: {}
};

QueueRow.propTypes = {
  contact: PropTypes.shape({
    priorities: PropTypes.arrayOf(PropTypes.shape({
      oldest: PropTypes.string,
      count: PropTypes.number,
      queueName: PropTypes.string,
      priority: PropTypes.number
    })),
    queueName: PropTypes.string,
    count: PropTypes.number,
    oldest: PropTypes.string,
    id: PropTypes.string
  }),
  columns: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string,
    value: PropTypes.func,
    isExpander: PropTypes.bool,
    isQueueColumn: PropTypes.bool
  }))
};

QueueRow.defaultProps = {
  contact: [],
  columns: []
};

export { QueueSummaryTable, QueueSummaryTable as default };
